// RulesPage.js
import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import PageHero from './PageHero';
import { getAllCMSImages } from '../services/s3Service';
import { getFirstCachedImage } from '../utils/cmsCache';

const RulesPage = () => {
  const [heroImage, setHeroImage] = useState(() => getFirstCachedImage(['page_hero_rules']));
  useEffect(() => {
    getAllCMSImages().then((imgs) => setHeroImage(imgs.page_hero_rules || null))
      .catch(() => setHeroImage(null));
  }, []);

  // Colores de la marca ACHIPO
  const colors = {
    primary: '#1a1a1a',       // Negro para texto principal
    secondary: '#666',        // Gris para texto secundario
    accent: '#D52B1E',        // Rojo bandera Chile
    accentLight: '#ef4444',  // Rojo claro para hover
    accentDark: '#b91c1c',   // Rojo oscuro
    lightGray: '#f8f9fa',     // Fondo claro
    mediumGray: '#e5e7eb',   // Bordes
    darkGray: '#374151',     // Fondo oscuro
    white: '#ffffff',
    gradient: 'linear-gradient(135deg, #D52B1E 0%, #0039A6 100%)',
  };

  const documents = [
    {
      id: 'ipl',
      title: 'International Powerlifting League',
      subtitle: 'Reglamento Oficial 2025',
      color: colors.accent,
      link: 'https://drive.google.com/uc?export=download&id=1r3ttA1IDHHiIf0xOLGBzsLOphMEPAECP',
      badge: 'IPL'
    },
    {
      id: 'wrpf-tecnico',
      title: 'Reglamento Técnico WRPF',
      subtitle: 'Normas técnicas oficiales',
      color: colors.darkGray,
      link: 'https://drive.google.com/uc?export=download&id=16JFQbJVlI4kI0bRUJvJtdX2_oO314GKP',
      badge: 'WRPF'
    },
    {
      id: 'wrpf-classification',
      title: 'Classification Standards WRPF',
      subtitle: 'Estándares de clasificación',
      color: colors.darkGray,
      link: 'https://drive.google.com/uc?export=download&id=1emyIsOfK1n-8khGz7qLedPLBaFGcxw_D',
      badge: 'WRPF'
    },
    {
      id: 'wrpf-niveles',
      title: 'Niveles Deportivos WRPF',
      subtitle: 'Actualizado',
      color: colors.darkGray,
      link: 'https://drive.google.com/uc?export=download&id=1aKFJjl26BkO16f4mJgj_LfofJV1vHuRB',
      badge: 'WRPF'
    }
  ];

  return (
    <div style={{ 
      fontFamily: 'Arial, sans-serif', 
      width: '100%', 
      margin: '0', 
      backgroundColor: colors.white, 
      minHeight: '100vh',
      color: colors.primary
    }}>
      <Header />
      <PageHero
        eyebrow="Compite"
        imageUrl={heroImage}
        title="Reglamentos Oficiales"
        subtitle="Normas y protocolos oficiales para competencias de powerlifting"
      />
      <div style={{ 
        padding: '0 20px 80px', 
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Grid de Reglamentos Moderno */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
          gap: '20px',
          marginBottom: '60px'
        }}>
          {documents.map((doc) => (
            <div 
              key={doc.id}
            style={{ 
              backgroundColor: colors.white,
              border: `2px solid ${colors.mediumGray}`,
              borderRadius: '12px',
              padding: '0',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              cursor: 'pointer',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: '240px'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = doc.id === 'ipl' 
                  ? '0 8px 16px rgba(220, 38, 38, 0.15)' 
                  : '0 8px 16px rgba(0, 0, 0, 0.15)';
                e.currentTarget.style.borderColor = doc.color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = colors.mediumGray;
              }}
            >
              {/* Badge */}
              <div style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                backgroundColor: doc.color,
                color: colors.white,
                padding: '4px 10px',
                borderRadius: '16px',
                fontSize: '0.65em',
                fontWeight: '700',
                letterSpacing: '0.5px',
                zIndex: 2
              }}>
                {doc.badge}
              </div>

              {/* Header con gradiente */}
              <div style={{
                background: doc.id === 'ipl' 
                  ? colors.gradient 
                  : `linear-gradient(135deg, ${colors.darkGray} 0%, #1f2937 100%)`,
                padding: '20px',
                textAlign: 'center',
                color: colors.white,
                flexShrink: 0,
                minHeight: '140px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <h3 style={{ 
                  fontSize: '1.1em', 
                  margin: '0 0 6px 0',
                  fontWeight: '700',
                  lineHeight: '1.2'
                }}>
                  {doc.title}
                </h3>
                <p style={{ 
                  fontSize: '0.8em',
                  opacity: 0.9,
                  margin: 0,
                  fontWeight: '500'
                }}>
                  {doc.subtitle}
                </p>
              </div>

              {/* Contenido */}
              <div style={{ 
                padding: '0px 18px 4px 18px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                flexGrow: 1
              }}>
                <a 
                  href={doc.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: doc.color,
                    color: colors.white,
                    padding: '8px 18px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '0.9em',
                    transition: 'all 0.2s ease',
                    width: '100%',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = doc.id === 'ipl' ? colors.accentLight : '#4b5563';
                    e.target.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = doc.color;
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  Descargar PDF
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Información importante */}
        <div style={{ 
          backgroundColor: '#fff5f5',
          border: `2px solid ${colors.accent}`,
          borderRadius: '12px',
          padding: '15px 20px',
          marginBottom: '50px'
        }}>
          <div>
            <h3 style={{ 
              color: colors.primary, 
              fontSize: '1.1em', 
              marginBottom: '8px',
              fontWeight: '700'
            }}>
              Información Importante
            </h3>
            <p style={{ 
              color: colors.secondary, 
              lineHeight: '1.6', 
              marginBottom: '6px',
              fontSize: '0.9em'
            }}>
              Los reglamentos establecen las normas técnicas, equipamiento permitido y protocolos 
              oficiales para competencias de powerlifting.
            </p>
            <p style={{ 
              color: colors.secondary, 
              fontSize: '0.85em',
              fontStyle: 'italic',
              fontWeight: '500',
              margin: 0
            }}>
              <strong>Nota:</strong> Verifica siempre la versión más reciente antes de competir.
            </p>
          </div>
        </div>
        
        {/* Sección de ayuda */}
        <div style={{ 
          backgroundColor: colors.lightGray,
          borderRadius: '16px',
          padding: '30px',
          border: `1px solid ${colors.mediumGray}`,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <h4 style={{ 
            color: colors.primary, 
            fontSize: '1.2em',
            margin: '0 0 15px 0',
            fontWeight: '700'
          }}>
            ¿Necesitas ayuda para interpretar los reglamentos?
          </h4>
          <p style={{ 
            color: colors.secondary, 
            lineHeight: '1.7', 
            marginBottom: '20px',
            fontSize: '0.95em',
            maxWidth: '600px'
          }}>
            Revisa los documentos oficiales más abajo para conocer las normativas en detalle. 
            Participar en eventos oficiales es la mejor forma de familiarizarte con su aplicación en competencia.
          </p>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: '15px',
            justifyContent: 'center'
          }}>
            
            <a 
              href="/events" 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: colors.white,
                color: colors.darkGray,
                padding: '12px 24px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '1em',
                transition: 'all 0.2s ease',
                border: `2px solid ${colors.darkGray}`
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#fff5f5';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colors.white;
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Próximos Eventos
            </a>

            <a 
              href="/records" 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: colors.white,
                color: colors.darkGray,
                padding: '12px 24px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '1em',
                transition: 'all 0.2s ease',
                border: `2px solid ${colors.darkGray}`
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#fff5f5';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colors.white;
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Ver Récords
            </a>
          </div>
        </div>

        {/* Información adicional */}
        <div style={{ 
          marginTop: '60px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '25px'
        }}>
          <div style={{ 
            backgroundColor: colors.white,
            padding: '30px',
            borderRadius: '12px',
            border: `1px solid ${colors.mediumGray}`,
            borderTop: `4px solid ${colors.accent}`
          }}>
            <h4 style={{ 
              color: colors.primary, 
              fontSize: '1.2em',
              marginBottom: '12px',
              fontWeight: '700'
            }}>
              Actualizaciones
            </h4>
            <p style={{ 
              color: colors.secondary, 
              fontSize: '0.95em',
              lineHeight: '1.7',
              margin: 0
            }}>
              Los reglamentos se actualizan periódicamente. Recomendamos revisar 
              las fechas de versión antes de cualquier competencia.
            </p>
          </div>

          <div style={{ 
            backgroundColor: colors.white,
            padding: '30px',
            borderRadius: '12px',
            border: `1px solid ${colors.mediumGray}`,
            borderTop: `4px solid ${colors.accent}`
          }}>
            <h4 style={{ 
              color: colors.primary, 
              fontSize: '1.2em',
              marginBottom: '12px',
              fontWeight: '700'
            }}>
              Certificación
            </h4>
            <p style={{ 
              color: colors.secondary, 
              fontSize: '0.95em',
              lineHeight: '1.7',
              margin: 0
            }}>
              Todos los jueces deben estar certificados y conocer los reglamentos 
              actualizados para poder oficiar en competencias oficiales.
            </p>
          </div>

          <div style={{ 
            backgroundColor: colors.white,
            padding: '30px',
            borderRadius: '12px',
            border: `1px solid ${colors.mediumGray}`,
            borderTop: `4px solid ${colors.accent}`
          }}>
            <h4 style={{ 
              color: colors.primary, 
              fontSize: '1.2em',
              marginBottom: '12px',
              fontWeight: '700'
            }}>
              Equipamiento
            </h4>
            <p style={{ 
              color: colors.secondary, 
              fontSize: '0.95em',
              lineHeight: '1.7',
              margin: 0
            }}>
              Verifica las especificaciones de equipamiento permitido según cada 
              federación y modalidad de competencia.
            </p>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default RulesPage;
